import type { GroupSearchResultItem, Guide, SearchResultItem } from '../../types';
import { LearnerSearchResult } from '../LearnerSearchResult';
import { CMS_RESULTS, SearchResultContentTypes, SectionTypes } from '../../constants';
import { CMSSearchResult } from '../CMSSearchResult';

export type SearchResultsProps = {
  results: SearchResultItem[];
  resultTarget?: '_self' | '_blank';
};

type GroupedResults = Map<string, GroupSearchResultItem>;

export const getLatestUpdatedInfo = (
  results: GroupedResults,
  result: SearchResultItem,
): [number, Guide['updated_by']] => {
  const isNotAGuide = result.type === SectionTypes.GUIDE_SECTION || result.type === SectionTypes.GUIDE_TASK;
  const guideId = isNotAGuide ? (result.guide?.id ?? result.id) : result.id;
  let guideUpdatedDate = isNotAGuide ? result.guide?.updated_at : result.updatedDate;
  let guideUpdatedBy = isNotAGuide ? result.guide?.updated_by : result.updatedBy;

  if (results.has(guideId)) {
    const existingGuide = results.get(guideId);
    if (existingGuide?.updatedDate && guideUpdatedDate && existingGuide?.updatedBy && guideUpdatedBy) {
      if (new Date(existingGuide.updatedDate).getTime() > new Date(guideUpdatedDate).getTime()) {
        guideUpdatedDate = existingGuide.updatedDate;
        guideUpdatedBy = existingGuide.updatedBy;
      }
    }
  }

  return [guideUpdatedDate ?? 0, guideUpdatedBy];
};

export const SearchResults = ({ results, resultTarget }: SearchResultsProps) => {
  const groupedResults = results.reduce((acc, result) => {
    if (
      result.type === SectionTypes.GUIDE_SECTION ||
      result.type === SectionTypes.GUIDE_TASK ||
      result.type === SearchResultContentTypes.GUIDE
    ) {
      const isNotAGuide = result.type === SectionTypes.GUIDE_SECTION || result.type === SectionTypes.GUIDE_TASK;
      const guideId = isNotAGuide ? result.guide?.id : result.id;
      const guideName = isNotAGuide ? result.guide?.name : result.name;
      const guideDescription = isNotAGuide ? result.guide?.description : result.description;
      const [guideUpdatedDate, guideUpdatedBy] = getLatestUpdatedInfo(acc, result);
      const children = isNotAGuide ? [result] : [];

      if (!acc.has(guideId)) {
        acc.set(guideId, {
          id: guideId,
          type: 'guide',
          name: guideName,
          description: guideDescription,
          updatedDate: guideUpdatedDate,
          updatedBy: guideUpdatedBy,
          snippetMatch: result.snippetMatch,
          children,
        });
      } else if (isNotAGuide) {
        acc.get(guideId).children.push(result);
      }
      return acc;
    }
    acc.set(result.id, {
      ...result,
      children: [],
    });
    return acc;
  }, new Map<string, GroupSearchResultItem>());

  return (
    <>
      {Array.from(groupedResults).map(([id, result]) =>
        CMS_RESULTS.includes(result.type) ? (
          <CMSSearchResult key={id} resultItem={result} resultTarget={resultTarget} />
        ) : (
          <LearnerSearchResult key={id} resultItem={result} resultTarget={resultTarget} />
        ),
      )}
    </>
  );
};
